
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { NextPage } from "next";
import { useRouter } from "next/dist/client/router";
import { useCallback, useContext, useEffect, useState } from "react";

import PageContainer from "components/common/PageContainer/PageContainer";
import PageBody from "components/common/PageContainer/PageBody/PageBody";
import classes from "styles/SearchMassage.module.css";
import AnnouncementsLazyList from "components/AnnouncementList/AnnouncementsLazyList";
import { AnnouncementDetails } from "utils/interfaces/commonInterfacesAndTypes";
import { PROFILE_DETAILS, SEARCH_MASSAGE } from "constants/routes";
import NotificationContext from "context/notification/notification-context";
import { decodeParams } from "utils/functions/encodeDecodeURI";
import { extractPriceFilterValues } from "utils/functions/extractFilterValues";
import NoAnnouncementsInfo from "components/MassageMasterAnnouncements/NoAnnouncementsInfo/NoAnnouncementsInfo";
import useTranslation from "next-translate/useTranslation";
import LocationContext from "context/location/location-context";
import {
  codeToCountry,
  countryPlaceIdToCode,
} from "utils/functions/placeFunctions";
import PlaceContext from "context/place/place-context";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import PartnerProfileApi from "../../user/partner/PartnerProfileApi";
import SearchPartnerFilters from "../../user/partner/SearchPartnerFilters";
import { GenderTypeKey } from "../../user/partner/Gender";
import { PaymentMethodKey } from "../../user/partner/PaymentMethod";
import { ClientTypeKey } from "../../user/client/ClientType";
import { WorkplaceKey } from "../../user/partner/Workplace";
import { AvailabilityKey } from "../../user/partner/Availability";
import { useSearchPartnerStore } from "../../context/partner/searchPartnerStore";
import FindAllProfilesResponse from "../../user/partner/FindAllProfilesResponse";
import { MassageCategoryKey } from "../../user/partner/MassageCategoryKey";
import SearchPagination from "../../components/Search/SearchPagination";

const SearchMassage: NextPage = () => {
  const { page: storePage } = useSearchPartnerStore();
  const { t } = useTranslation("common");
  const { display } = useContext(NotificationContext);
  const { location } = useContext(LocationContext);
  const router = useRouter();

  const {
    cityId,
    payment,
    accessToTheCustomer,
    genders,
    price,
    priceFrom,
    priceTo,
    category,
    clientTypes,
    typeOfSalons,
    facilitateAccess,
    page,
  } = router.query;

  const loadByCountry =
    cityId &&
    countryPlaceIdToCode(decodeParams(cityId)[0]) !== decodeParams(cityId)[0];
  const [loadAllWithCountryPriority, setLoadAllWithCountryPriority] =
    useState<boolean>(false);

  // const [isLoading, setIsLoading] = useState(false);
  const [announcements, setAnnouncements] = useState<AnnouncementDetails[]>([]);
  // const [page, setPage] = useState<number>(0);
  const [loadedAll, setLoadedAll] = useState<boolean>(false);
  const [previousPage, setPreviousPage] = useState<number>(0);

  const { place } = useContext(PlaceContext);
  const placeName = place?.city || place?.country || "";

  const createAnnouncementFilter = useCallback(() => {
    const decodedPayment = payment && decodeParams(payment);
    const decodedGenders = genders && decodeParams(genders);
    const decodedClientTypes = clientTypes && decodeParams(clientTypes);
    const decodedTypeOfSalons = typeOfSalons && decodeParams(typeOfSalons);
    const decodedFacilitateAccess =
      facilitateAccess && decodeParams(facilitateAccess);
    const priceFilterValues =
      price && extractPriceFilterValues(price as string);
    const decodedCategory = category && decodeParams(category);
    const decodedCommuteToCustomer =
      accessToTheCustomer && decodeParams(accessToTheCustomer);

    const filter: SearchPartnerFilters = {
      priceFrom: priceFilterValues
        ? priceFilterValues.priceFromValue
        : undefined,
      priceTo: priceFilterValues ? priceFilterValues.priceToValue : undefined,
      paymentMethods: decodedPayment
        ? (decodedPayment as PaymentMethodKey[])
        : undefined,
      isVisitingClient: decodedCommuteToCustomer
        ? decodedCommuteToCustomer === "true"
        : undefined,
      genders: decodedGenders ? (decodedGenders as GenderTypeKey[]) : undefined,
      clientTypes: decodedClientTypes
        ? (decodedClientTypes as ClientTypeKey[])
        : undefined,
      workplaces: decodedTypeOfSalons
        ? (decodedTypeOfSalons as WorkplaceKey[])
        : undefined,
      availabilities: decodedFacilitateAccess
        ? (decodedFacilitateAccess as AvailabilityKey[])
        : undefined,
      serviceCategory: decodedCategory
        ? (decodedCategory as MassageCategoryKey)
        : undefined,
      city: cityId ? (decodeParams(cityId) as string) : undefined,
      page: page ? Number(page) : 1,
    };
    return filter;
  }, [
    page,
    cityId,
    payment,
    loadAllWithCountryPriority,
    accessToTheCustomer,
    genders,
    price,
    category,
    clientTypes,
    facilitateAccess,
    typeOfSalons,
    router.query,
  ]);

  const { isLoading, data, error } = useQuery<FindAllProfilesResponse>({
    queryKey: ["partner-profile-search", page],
    queryFn: async () => {
      try {
        const filters = createAnnouncementFilter();
        console.log("filters", filters);
        const res = await new PartnerProfileApi().search({
          ...filters,
          previousPage: previousPage,
        });
        setPreviousPage(res.pageNumber);
        return res;
      } catch (e) {
        const error = e as AxiosError;
        console.log(error?.response?.data);
        throw e;
      }
    },
    retry: false,
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const cleanLoadAnnouncements = () => {
      setAnnouncements([]);
      // setPage(0);
      setLoadedAll(false);
      // setIsLoading(false);
    };

    setLoadAllWithCountryPriority(false);
    cleanLoadAnnouncements();
    window.scrollTo(0, 0);
  }, [router.isReady, router.query]);

  useEffect(() => {
    if (location.countryCode && cityId === undefined) {
      const country = codeToCountry(location.countryCode);
      router.query.cityId = country;
      router.replace(router);
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickAnnouncementHandler = (announcementId: string) =>
    router.push(`${PROFILE_DETAILS}/${announcementId}`);

  const appendAllCountryAnnouncements = useCallback(() => {
    if (announcements.length || loadedAll) {
      setLoadAllWithCountryPriority(true);
      setLoadedAll(false);
      // setPage(0);
    }
  }, [announcements, loadedAll]);

  const searchListTitle = `${t("results")} (${data?.totalCount || 0})`;

  return (
    <PageContainer className={classes.pageContainer}>
      <PageBody>
        <>
          <AnnouncementsLazyList
            items={data?.results}
            onClickAnnouncement={onClickAnnouncementHandler}
            title={searchListTitle}
            verifiedLabelVisible={true}
            isLoading={isLoading}
            loadedAll={loadedAll}
          />
          {!!data?.results.length ? (
            <div className={classes.paginationContainer}>
              <SearchPagination
                page={page ? Number(page) : 1}
                totalPages={data.pages}
                onPrevClick={() =>
                  router.push(
                    {
                      pathname: SEARCH_MASSAGE,
                      query: {
                        ...router.query,
                        page: Number(page || 1) - 1,
                      },
                    },
                    undefined,
                    { shallow: true }
                  )
                }
                onNextClick={() =>
                  router.push(
                    {
                      pathname: SEARCH_MASSAGE,
                      query: {
                        ...router.query,
                        page: Number(page || 1) + 1,
                      },
                    },
                    undefined,
                    { shallow: true }
                  )
                }
              />
            </div>
          ) : null}
          {!isLoading && !data?.results.length ? (
            <div className={classes.noAnnouncementsInfoContainer}>
              <NoAnnouncementsInfo />
            </div>
          ) : null}
        </>
      </PageBody>
    </PageContainer>
  );
};

export default SearchMassage;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/search-massage/index',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  